import React, { useState, useEffect, useMemo } from 'react';
import { X, Calendar, Trophy, Lock, Sparkles, ChevronRight, TrendingUp, Zap } from 'lucide-react';
import { DS, GS, hairline, eyebrow, cardShadow } from '../../lib/designTokens';
import { isGameLive } from '../../lib/game-utils';
import { GameStrip } from './GameStrip';
import { MatchupHero } from './MatchupHero';
import { collection, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { buildOpponentScout } from '../../lib/insights/opponent-signals';
import { buildFirstLook } from '../../lib/first-look';

export const HomeTab = ({
  team, games, players, tournaments,
  recordString, teamLeaders, hotPlayers, playerSeasonStats, completedGames,
  isHeadCoach, effectiveIsElite,
  welcomeInitialUnseen, welcomeDismissed, setWelcomeDismissed,
  onPlanGame, onLive, onOpenTab, onAddGame, onCreateTournament,
  onScoutRoster, onCalendarSync, onStatsImport, onUploadGameStats, onUpgrade,
  onOpenTournament, onSmartScout, devReviewCount, onOpenDevReview,
}: {
  team: any;
  games: any[];
  players: any[];
  tournaments: any[];
  recordString: string;
  teamLeaders: any[];
  hotPlayers: any[];
  playerSeasonStats: Map<string, any>;
  completedGames: any[];
  isHeadCoach: boolean;
  effectiveIsElite: boolean;
  welcomeInitialUnseen: boolean | null;
  welcomeDismissed: boolean;
  setWelcomeDismissed: (v: boolean) => void;
  onPlanGame: (game: any, tab?: 'gameplan' | 'bullpen' | 'scouting' | 'lineup' | 'defense') => void;
  onLive: (game: any) => void;
  onOpenTab: (tab: string) => void;
  onAddGame: () => void;
  onCreateTournament: () => void;
  onScoutRoster: () => void;
  onCalendarSync: () => void;
  onStatsImport: () => void;
  onUploadGameStats: (game: any) => void;
  onUpgrade: () => void;
  onOpenTournament: (tournamentId: string) => void;
  onSmartScout: () => void;
  devReviewCount: number;
  onOpenDevReview: () => void;
}) => {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  // ── Hero game selection ──────────────────────────────────────────────────
  const defaultGameId = useMemo(() => {
    const live = games.find(isGameLive);
    if (live) return live.id;
    const next = games
      .filter((g: any) => g.status === 'scheduled' && new Date(g.date) >= startOfToday)
      .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())[0];
    if (next) return next.id;
    const last = games
      .filter((g: any) => g.status === 'completed')
      .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
    return last?.id ?? null;
  }, [games]);

  const [selectedGameId, setSelectedGameId] = useState<string | null>(defaultGameId);

  useEffect(() => {
    // Re-derive when games change and the current selection vanished (or never set)
    if (!selectedGameId || !games.some((g: any) => g.id === selectedGameId)) {
      setSelectedGameId(defaultGameId);
    }
  }, [defaultGameId, games, selectedGameId]);

  const selectedGame = games.find((g: any) => g.id === selectedGameId) || null;

  // ── Opponent records (lightweight — no at-bat load needed) ──────────────
  const [opponentRecords, setOpponentRecords] = useState<Map<string, any>>(new Map());
  useEffect(() => {
    if (!team?.id) return;
    const unsub = onSnapshot(collection(db, `teams/${team.id}/opponents`), snap => {
      const m = new Map<string, any>();
      snap.forEach(d => m.set(d.id, d.data()));
      setOpponentRecords(m);
    });
    return unsub;
  }, [team?.id]);

  const opponentIntel = useMemo(() => {
    if (!selectedGame || selectedGame.status === 'completed') return null;
    const norm = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const target = norm(selectedGame.opponent || '');
    if (!target) return null;
    const rec = [...opponentRecords.entries()].find(([k]) =>
      k === target || k.includes(target) || target.includes(k)
    )?.[1];
    if (!rec) return null;
    // Records written by the old scorebook importer stored gameIds as a map
    // ({_methodName, _elements}) instead of an array; count either shape.
    const ids = Array.isArray(rec.gameIds) ? rec.gameIds : (rec.gameIds?._elements || []);
    const meetings: number = ids.length;
    const knownPlayers: number = Object.keys(rec.players || {}).length;
    return { meetings, knownPlayers, opponentName: rec.name || selectedGame.opponent };
  }, [selectedGame, opponentRecords]);

  // Lazily load this opponent's top danger bat for the Home preview — only when
  // we've actually faced them before (meetings > 0), and only for the one
  // selected game (no fan-out across the schedule). Same opponent-key matching
  // as PlanGameView so Home and the full scout never disagree.
  const [topThreat, setTopThreat] = useState<{ name: string; number: string } | null>(null);
  useEffect(() => {
    setTopThreat(null);
    if (!team?.id || !selectedGame || !opponentIntel || opponentIntel.meetings === 0) return;
    let live = true;
    (async () => {
      try {
        const norm = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const target = norm(selectedGame.opponent || '');
        if (!target) return;
        const gamesSnap = await getDocs(collection(db, `teams/${team.id}/games`));
        const all: any[] = [];
        await Promise.all(gamesSnap.docs.map(async (g) => {
          const abSnap = await getDocs(collection(db, `teams/${team.id}/games/${g.id}/atBats`));
          abSnap.forEach((d) => all.push({ ...(d.data() as any), gameId: g.id }));
        }));
        if (!live) return;
        const keys = [...new Set(all.map(a => a.opponentKey).filter(Boolean))] as string[];
        const matchKey = keys.find(k => k === target || k.includes(target) || target.includes(k));
        if (!matchKey) return;
        const scout = buildOpponentScout(all as any, matchKey);
        const danger = scout.batters.find(b => b.danger);
        if (live && danger) setTopThreat({ name: danger.name, number: danger.number });
      } catch { /* non-fatal — preview just won't show */ }
    })();
    return () => { live = false; };
  }, [team?.id, selectedGame, opponentIntel]);

  // ── Welcome / checklist visibility (logic identical to the old branch) ───
  const cl = team?.settings?.onboardingChecklist;
  const allDoneForWelcome = !!(cl && cl.rosterScouted && cl.calendarSynced && cl.statsUploaded && cl.firstGamePlanned);
  const welcomeVisible = !!(
    isHeadCoach
    && !team?.isDemoSession
    && team?.createdAt
    && !allDoneForWelcome
    && welcomeInitialUnseen === true
    && !welcomeDismissed
  );

  const pitcherCount = players.filter((p: any) => (p.attributes?.pitching?.velocity ?? 0) > 0 || (p.attributes?.pitching?.control ?? 0) > 0).length;
  const upcomingCount = games.filter((g: any) => g.status === 'scheduled').length;

  // ── Season aggregates (math identical to old record banner) ──────────────
  const seasonAgg = useMemo(() => {
    let totalAb = 0, sumAvgAb = 0, totalPa = 0, sumOpsPa = 0, totalH = 0, totalBb = 0;
    playerSeasonStats.forEach((s: any) => {
      totalH += s.h; totalBb += s.bb;
      sumAvgAb += s.avg * s.ab; totalAb += s.ab;
      sumOpsPa += s.ops * s.pa; totalPa += s.pa;
    });
    return {
      avg: totalAb > 0 ? (sumAvgAb / totalAb).toFixed(3).replace(/^0/, '') : '—',
      ops: totalPa > 0 ? (sumOpsPa / totalPa).toFixed(3).replace(/^0/, '') : '—',
      h: totalH,
      bb: totalBb,
      hasData: totalPa > 0,
    };
  }, [playerSeasonStats]);

  // ── Scout's First Look (time-to-wow) ─────────────────────────────────────
  // Real insight cards computed from the stats the coach imported in the
  // wizard, shown until Dugout has real game data of its own. Head coach only,
  // never in the demo (the demo ships with full intel already).
  const firstLook = useMemo(() => buildFirstLook(players), [players]);
  const firstLookVisible = !!(
    isHeadCoach && !team?.isDemoSession && firstLook && completedGames.length === 0
  );

  // ── Getting Started steps (logic identical) ──────────────────────────────
  // Scouting leads the list on purpose: it's the fastest path to the ranked-
  // roster reveal (the "wow"), while calendar sync and stats upload are chores.
  const checklistSteps = (!isHeadCoach || team?.isDemoSession || !cl || allDoneForWelcome) ? null : [
    {
      key: 'rosterScouted',
      done: cl.rosterScouted,
      label: 'Scout your roster',
      sub: 'Rate each player 1–5. Watch your xOVR scores come alive.',
      cta: 'Scout Now',
      onTap: onScoutRoster,
    },
    {
      key: 'calendarSynced',
      done: cl.calendarSynced,
      label: 'Sync your schedule',
      sub: 'Paste your GameChanger calendar link to pull in your season.',
      cta: 'Sync Calendar',
      onTap: onCalendarSync,
    },
    {
      key: 'statsUploaded',
      done: cl.statsUploaded,
      label: 'Upload prior season stats',
      sub: 'Import from GameChanger so xOVR has real data to work with.',
      cta: 'Upload Stats',
      onTap: () => { if (effectiveIsElite) onStatsImport(); else onUpgrade(); },
      elite: true,
    },
    {
      key: 'firstGamePlanned',
      done: cl.firstGamePlanned,
      label: 'Plan your first game',
      sub: 'Build a lineup powered by your scouting and stats.',
      cta: 'Plan a Game',
      onTap: () => {
        const next = games
          .filter((g: any) => g.status === 'scheduled' && new Date(g.date) >= startOfToday)
          .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())[0];
        if (next) onPlanGame(next);
        else onAddGame();
      },
    },
  ];
  const doneCount = checklistSteps ? checklistSteps.filter(s => s.done).length : 0;

  const sectionHeader = (label: string, action?: { label: string; onTap: () => void }) => (
    <div style={{ padding: '14px 16px 6px', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
      <span style={eyebrow}>{label}</span>
      {action && (
        <button onClick={action.onTap} style={{ fontSize: '11px', fontWeight: 600, color: DS.textSec, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          {action.label}
        </button>
      )}
    </div>
  );

  // Two-layer surface system: cool-slate canvas behind, white cards in front.
  // The card shadow, not a border, is what lifts each section off the canvas.
  // DS.bgAlt (#F7F8F9) matches the Stats panel so Home reads on the same surface.
  const CANVAS = DS.bgAlt;
  const card: React.CSSProperties = {
    backgroundColor: '#FFFFFF',
    boxShadow: cardShadow,
    borderRadius: '16px',
    margin: '12px 16px 0',
    overflow: 'hidden',
  };

  return (
    <div style={{ backgroundColor: CANVAS, minHeight: '100%', paddingBottom: '8px' }}>

      {/* ── Game strip + matchup hero (one card) ───────────────────────── */}
      {/* Extra top margin so the first card breathes below the sticky app header. */}
      <div style={{ ...card, marginTop: '20px' }}>
        <GameStrip
          team={team}
          games={games}
          selectedGameId={selectedGameId}
          onSelect={(g: any) => setSelectedGameId(g.id)}
          onCalendar={() => onOpenTab('games')}
        />
        {selectedGame ? (
          <MatchupHero
            game={selectedGame}
            team={team}
            recordString={recordString}
            players={players}
            onPlan={(g: any) => onPlanGame(g, 'gameplan')}
            onScouting={(g: any) => onPlanGame(g, 'scouting')}
            onLive={onLive}
            onUploadGameStats={onUploadGameStats}
            onOpenTournament={onOpenTournament}
            opponentIntel={opponentIntel}
            topThreat={topThreat}
          />
        ) : (
          <div style={{ padding: '28px 16px', textAlign: 'center' }}>
            <p style={{ fontSize: '13px', fontWeight: 400, color: DS.textSec }}>No games on the schedule yet.</p>
            <button
              onClick={onAddGame}
              style={{ marginTop: '10px', padding: '8px 16px', borderRadius: '8px', backgroundColor: DS.textPri, color: '#FFFFFF', fontSize: '12px', fontWeight: 600, border: 'none', cursor: 'pointer' }}
            >
              Add a Game
            </button>
          </div>
        )}
      </div>

      {/* ── Development Review alert ───────────────────────────────────── */}
      {isHeadCoach && devReviewCount > 0 && (
        <button
          onClick={onOpenDevReview}
          style={{
            ...card,
            display: 'flex', alignItems: 'center', gap: '12px',
            // `card` provides 16px side margins. A <button> is inline-block, so it
            // won't auto-fill the container the way the sibling <div> cards do —
            // but `width: 100%` ignores the margins and overflows the right edge.
            // Fill the inset area explicitly: 100% minus the two 16px margins.
            padding: '14px 16px', width: 'calc(100% - 32px)', boxSizing: 'border-box',
            background: DS.bg, border: 'none', cursor: 'pointer', textAlign: 'left',
          }}
        >
          <div style={{
            width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
            backgroundColor: GS.greenSoft,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <TrendingUp size={17} color={GS.greenInk} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: DS.textPri }}>
              Development Review
            </div>
            <div style={{ fontSize: '11px', color: DS.textSec, marginTop: '2px' }}>
              {devReviewCount} player{devReviewCount !== 1 ? 's' : ''}: xOVR has diverged from scouted ratings
            </div>
          </div>
          <div style={{
            backgroundColor: GS.green, color: '#FFFFFF',
            borderRadius: '12px', padding: '3px 9px',
            fontSize: '12px', fontWeight: 700, flexShrink: 0,
          }}>
            {devReviewCount}
          </div>
        </button>
      )}

      {/* ── Welcome card ───────────────────────────────────────────────── */}
      {welcomeVisible && (
        <div style={{ margin: '14px 16px 0', boxShadow: cardShadow, borderRadius: '16px', position: 'relative', backgroundColor: DS.bg }}>
          <button
            onClick={() => {
              if (team?.id) {
                try { localStorage.setItem(`dugout_welcomeSeen_${team.id}`, 'true'); } catch {}
              }
              setWelcomeDismissed(true);
            }}
            aria-label="Dismiss welcome"
            style={{ position: 'absolute', top: '10px', right: '10px', padding: '4px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex' }}
          >
            <X style={{ width: '14px', height: '14px', color: DS.textMuted }} />
          </button>
          <div style={{ padding: '14px 14px 10px' }}>
            <div style={{ ...eyebrow, fontSize: '10px', marginBottom: '3px' }}>Welcome to {team.name}</div>
            <div style={{ fontSize: '18px', fontWeight: 600, color: DS.textPri }}>Your team is ready.</div>
          </div>
          <div style={{ display: 'flex', borderTop: hairline }}>
            {[
              { value: players.length, label: 'Players' },
              { value: upcomingCount, label: 'Games' },
              { value: pitcherCount, label: 'Pitchers' },
            ].map(({ value, label }, i) => (
              <div key={label} style={{ flex: 1, padding: '10px 0', textAlign: 'center', borderLeft: i > 0 ? hairline : 'none' }}>
                <div style={{ fontSize: '20px', fontWeight: 600, color: DS.textPri, lineHeight: 1 }}>{value}</div>
                <div style={{ fontSize: '10px', fontWeight: 400, color: DS.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: '3px' }}>{label}</div>
              </div>
            ))}
          </div>
          {/* Scouting action lives in the Getting Started checklist below — the
              welcome card just sets the scene so we don't double up CTAs. */}
          <p style={{ fontSize: '11px', fontWeight: 400, color: DS.textMuted, textAlign: 'center', padding: '12px 14px', borderTop: hairline, lineHeight: 1.5, margin: 0 }}>
            {cl && !cl.rosterScouted
              ? '5 minutes of scouting unlocks xOVR for every player. Find it in Getting Started below. This is where Dugout comes alive.'
              : 'Pick up where you left off below.'}
          </p>
        </div>
      )}

      {/* ── Scout's First Look ─────────────────────────────────────────── */}
      {/* The first impressive thing a new coach sees: their own players'
          numbers turned into dugout intel, seconds after the wizard. */}
      {firstLookVisible && firstLook && (
        <div>
          <div style={{ padding: '14px 16px 0', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <span style={eyebrow}>Scout's First Look</span>
            <span style={{ fontSize: '11px', fontWeight: 400, color: DS.textMuted }}>
              {firstLook.source === 'prior' ? "last season's numbers" : 'from your stats'}
            </span>
          </div>
          <div style={{ ...card, marginTop: '8px' }}>
            {firstLook.cards.map((c, i) => (
              <div key={c.key} style={{ padding: '14px 16px', borderBottom: i < firstLook.cards.length - 1 ? hairline : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '9px', fontWeight: 700, color: GS.greenInk, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{c.tag}</div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: DS.textPri, marginTop: '2px' }}>{c.playerName}</div>
                    <div style={{ fontSize: '12px', color: DS.textSec, marginTop: '3px', lineHeight: 1.5 }}>{c.headline}</div>
                  </div>
                  <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                    <span style={{ fontSize: '9px', fontWeight: 600, color: DS.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c.statLabel}</span>
                    <span style={{ fontSize: '28px', fontWeight: 700, color: GS.greenInk, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{c.statValue}</span>
                  </div>
                </div>
              </div>
            ))}
            {cl && !cl.rosterScouted && (
              <button
                onClick={onScoutRoster}
                style={{
                  display: 'block', width: '100%', padding: '13px',
                  background: 'none', border: 'none',
                  borderTopStyle: 'solid', borderTopWidth: '0.5px', borderTopColor: DS.sep,
                  cursor: 'pointer', fontSize: '13px', fontWeight: 700, color: DS.textPri,
                  letterSpacing: '-0.01em',
                }}
              >
                This is the numbers alone. Add your scouting eye →
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Getting Started ────────────────────────────────────────────── */}
      {checklistSteps && (
        <div>
          <div style={{ padding: '14px 16px 0', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <span style={eyebrow}>Getting Started</span>
            <span style={{ fontSize: '11px', fontWeight: 400, color: DS.textMuted }}>{doneCount} of {checklistSteps.length} done</span>
          </div>
          <div style={{ ...card, marginTop: '8px' }}>
            <div style={{ margin: '12px 16px 4px', height: '3px', backgroundColor: DS.sep, borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${(doneCount / checklistSteps.length) * 100}%`, backgroundColor: DS.textPri, borderRadius: '2px', transition: 'width 0.5s' }} />
            </div>
            {checklistSteps.map((step, i) => (
              <div key={step.key} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', borderBottom: i < checklistSteps.length - 1 ? hairline : 'none' }}>
                {/* Status circle */}
                <div style={{
                  width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0,
                  backgroundColor: step.done ? DS.textPri : 'transparent',
                  border: step.done ? 'none' : `1px solid ${DS.sep}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {step.done && (
                    <svg style={{ width: '10px', height: '10px', color: '#FFFFFF' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: step.done ? DS.dim : DS.textPri }}>
                    {step.label}
                    {step.elite && !effectiveIsElite && (
                      <span style={{ marginLeft: '6px', fontSize: '9px', fontWeight: 600, color: DS.goldText, backgroundColor: DS.goldBg, padding: '1px 5px', borderRadius: '3px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Elite</span>
                    )}
                  </div>
                  {!step.done && (
                    <div style={{ fontSize: '12px', fontWeight: 400, color: DS.textSec, marginTop: '1px', lineHeight: 1.4 }}>{step.sub}</div>
                  )}
                </div>
                {!step.done && (
                  <button
                    onClick={step.onTap}
                    style={{ fontSize: '12px', fontWeight: 600, color: DS.textPri, background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, padding: 0 }}
                  >
                    {step.cta} →
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Sample insight preview (Time-to-Wow: shown before any players added) ── */}
      {welcomeVisible && players.length === 0 && (
        <div>
          <div style={{ padding: '14px 16px 6px', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <span style={eyebrow}>What You'll See</span>
            <span style={{ fontSize: '11px', fontWeight: 400, color: DS.textMuted }}>once scouted</span>
          </div>
          <div style={{ ...card, marginTop: '8px', overflow: 'visible' }}>
            {/* Preview header */}
            <div style={{ padding: '12px 16px 8px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: hairline }}>
              <Zap size={14} color={GS.green} />
              <span style={{ fontSize: '12px', fontWeight: 600, color: DS.textSec }}>Live sample: your real data will look like this</span>
            </div>

            {/* Sample insight 1: xOVR divergence */}
            <div style={{ padding: '14px 16px', borderBottom: hairline }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: DS.textPri }}>Rivera is outperforming his rating</div>
                  <div style={{ fontSize: '12px', color: DS.textSec, marginTop: '3px', lineHeight: 1.5 }}>
                    His xOVR is tracking <span style={{ fontWeight: 700, color: GS.greenInk }}>+7 pts above</span> his scouted OVR over 6 games. Consider bumping Contact to 4.
                  </div>
                </div>
                <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                  <span style={{ fontSize: '9px', fontWeight: 600, color: DS.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>xOVR</span>
                  <span style={{ fontSize: '28px', fontWeight: 700, color: GS.greenInk, lineHeight: 1 }}>81</span>
                  <span style={{ fontSize: '10px', color: DS.textMuted }}>vs OVR 74</span>
                </div>
              </div>
            </div>

            {/* Sample insight 2: pitching recommendation */}
            <div style={{ padding: '14px 16px', borderBottom: hairline }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: DS.textPri }}>Consider a reliever in the 4th</div>
                  <div style={{ fontSize: '12px', color: DS.textSec, marginTop: '3px', lineHeight: 1.5 }}>
                    Torres averages <span style={{ fontWeight: 700 }}>68 pitches through 3 innings</span>. Martinez has fresh legs (0 pitches last 4 days).
                  </div>
                </div>
                <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                  <span style={{ fontSize: '9px', fontWeight: 600, color: DS.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>PC</span>
                  <span style={{ fontSize: '28px', fontWeight: 700, color: DS.textPri, lineHeight: 1 }}>68</span>
                  <span style={{ fontSize: '10px', color: DS.textMuted }}>inning 3</span>
                </div>
              </div>
            </div>

            {/* Sample insight 3: hot streak */}
            <div style={{ padding: '14px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: DS.textPri }}>Johnson is on a tear</div>
                  <div style={{ fontSize: '12px', color: DS.textSec, marginTop: '3px', lineHeight: 1.5 }}>
                    <span style={{ fontWeight: 700 }}>.467 AVG over last 5 games</span> with 3 extra-base hits. Batting 3rd is earning him that slot.
                  </div>
                </div>
                <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                  <span style={{ fontSize: '9px', fontWeight: 600, color: DS.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>AVG</span>
                  <span style={{ fontSize: '28px', fontWeight: 700, color: DS.textPri, lineHeight: 1 }}>.467</span>
                  <span style={{ fontSize: '10px', color: DS.textMuted }}>L5 games</span>
                </div>
              </div>
            </div>

            {/* CTA footer */}
            <button
              onClick={onScoutRoster}
              style={{
                display: 'block', width: '100%', padding: '13px',
                borderTop: hairline, background: 'none', border: 'none',
                borderTopStyle: 'solid', borderTopWidth: '0.5px', borderTopColor: DS.sep,
                cursor: 'pointer', fontSize: '13px', fontWeight: 700, color: DS.textPri,
                letterSpacing: '-0.01em',
              }}
            >
              Scout your roster to unlock these insights →
            </button>
          </div>
        </div>
      )}

      {/* ── Season aggregates — white tiles lifted off the canvas ──────── */}
      {seasonAgg.hasData && (
        <div>
          {sectionHeader('Season')}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px', padding: '8px 16px 4px' }}>
            {[
              { value: seasonAgg.avg, label: 'Team AVG' },
              { value: seasonAgg.ops, label: 'Team OPS' },
              { value: seasonAgg.h, label: 'Hits' },
              { value: seasonAgg.bb, label: 'Walks' },
            ].map(({ value, label }) => (
              <div key={label} style={{ background: GS.panel, boxShadow: cardShadow, borderRadius: '16px', padding: '13px 4px', textAlign: 'center' }}>
                <div style={{ fontSize: '19px', fontWeight: 700, color: DS.textPri, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
                <div style={{ fontSize: '9px', fontWeight: 700, color: DS.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: '5px' }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Who's Hot — three-circle layout, like Top Performers ───────── */}
      {hotPlayers.length > 0 && (
        <div>
          {sectionHeader("Who's Hot", { label: 'View All', onTap: () => onOpenTab('stats') })}
          <div style={{ ...card, marginTop: '8px', padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
              {hotPlayers.slice(0, 3).map(({ player, hc, name }: any) => {
                const isHot = hc.batting === 'hot' || hc.pitching === 'hot';
                const emoji = isHot ? '🔥' : '❄️';
                return (
                  <div key={player.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, maxWidth: '130px', minWidth: 0 }}>
                    {/* Jersey avatar tile — hot = green fill, cold = cool blue */}
                    <div style={{
                      width: '58px', height: '58px', borderRadius: '16px',
                      background: isHot ? `linear-gradient(150deg, ${GS.green}, ${GS.green2})` : '#E4EEF4',
                      color: isHot ? '#FFFFFF' : '#3E7FA6',
                      boxShadow: isHot ? `0 6px 18px ${GS.greenGlow}` : 'none',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '19px', fontWeight: 800,
                      position: 'relative',
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {player.number}
                      <span style={{ position: 'absolute', bottom: '-6px', right: '-6px', fontSize: '16px' }}>{emoji}</span>
                    </div>
                    {/* Last name */}
                    <span style={{ fontSize: '14px', fontWeight: 700, color: DS.textPri, marginTop: '10px', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {(name || '').trim().split(/\s+/).filter(Boolean).pop() || name}
                    </span>
                    {/* Top signal */}
                    {hc.topSignals[0] && (
                      <span style={{ fontSize: '11px', fontWeight: 600, color: DS.textSec, marginTop: '2px', textAlign: 'center', lineHeight: 1.35 }}>
                        {hc.topSignals[0]}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Smart Scout ────────────────────────────────────────────────── */}
      <button
        onClick={() => effectiveIsElite ? onSmartScout() : onUpgrade()}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: 'calc(100% - 32px)', margin: '12px 16px 0',
          padding: '13px 14px', border: 'none', borderRadius: '16px',
          boxShadow: cardShadow, backgroundColor: '#FFFFFF', cursor: 'pointer',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Sparkles style={{ width: '18px', height: '18px', color: effectiveIsElite ? DS.textSec : DS.textMuted }} />
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: effectiveIsElite ? DS.textPri : DS.textSec }}>Smart Scout</div>
            <div style={{ fontSize: '11px', fontWeight: 400, color: DS.textMuted, marginTop: '1px' }}>
              {effectiveIsElite
                ? `${completedGames.filter((g: any) => g.stats).length} game${completedGames.filter((g: any) => g.stats).length !== 1 ? 's' : ''} · AI season insights`
                : 'Elite feature · AI season insights'}
            </div>
          </div>
        </div>
        {effectiveIsElite
          ? <ChevronRight style={{ width: '16px', height: '16px', color: DS.dim }} />
          : <Lock style={{ width: '14px', height: '14px', color: DS.textMuted }} />}
      </button>

      {/* ── Leaders — batting and pitching on separate rows ────────────── */}
      {(() => {
        const battingLeaders = teamLeaders.filter(({ cat }: any) => cat.color === 'emerald');
        const pitchingLeaders = teamLeaders.filter(({ cat }: any) => cat.color === 'sky');
        const leaderRow = (label: string, leaders: any[]) => leaders.length > 0 && (
          <div>
            {sectionHeader(label)}
            {/* A scroller clips its overflow, shadows included: pad it out for the
                card shadow and pull the margins back so the layout doesn't move. */}
            <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', padding: '8px 16px 20px', margin: '-4px 0 -12px' }} className="no-scrollbar">
              {leaders.map(({ cat, player, statValue }: any) => (
                <div
                  key={cat.id}
                  style={{ flexShrink: 0, width: '108px', boxShadow: cardShadow, borderRadius: '16px', padding: '12px', backgroundColor: DS.bg }}
                >
                  <div style={{ fontSize: '10px', fontWeight: 600, color: DS.textSec, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{cat.label}</div>
                  <div style={{ fontSize: '22px', fontWeight: 600, color: DS.textPri, lineHeight: 1, margin: '8px 0' }}>{cat.format(statValue)}</div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: DS.textPri, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{player.name.split(' ')[0]}</div>
                  <div style={{ fontSize: '11px', fontWeight: 400, color: DS.textMuted }}>#{player.number}</div>
                </div>
              ))}
            </div>
          </div>
        );
        return (
          <>
            {leaderRow('Batting Leaders', battingLeaders)}
            {leaderRow('Pitching Leaders', pitchingLeaders)}
          </>
        );
      })()}

      {/* ── Quick Actions ──────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '10px', padding: '12px 16px 24px' }}>
        <button
          onClick={onAddGame}
          style={{ flex: 1, padding: '16px 0', border: 'none', boxShadow: cardShadow, borderRadius: '16px', backgroundColor: '#FFFFFF', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}
        >
          <Calendar style={{ width: '20px', height: '20px', color: DS.textSec }} />
          <span style={{ fontSize: '11px', fontWeight: 600, color: DS.textPri, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Add Game</span>
        </button>
        <button
          onClick={onCreateTournament}
          style={{ flex: 1, padding: '16px 0', border: 'none', boxShadow: cardShadow, borderRadius: '16px', backgroundColor: '#FFFFFF', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', position: 'relative' }}
        >
          <div style={{ position: 'relative' }}>
            <Trophy style={{ width: '20px', height: '20px', color: DS.textSec }} />
            {!effectiveIsElite && <Lock style={{ width: '10px', height: '10px', color: DS.textMuted, position: 'absolute', bottom: '-2px', right: '-4px' }} />}
          </div>
          <span style={{ fontSize: '11px', fontWeight: 600, color: DS.textPri, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Add Tournament</span>
        </button>
      </div>

    </div>
  );
};
